import { SUPPORTED_LANGUAGE_CODES, DEFAULT_LANGUAGE, type LanguageCode } from './languages';

// The display label for a language, rendered as its endonym (the language's own name
// for itself, e.g. 'Español', 'Français', '日本語') via the platform's Intl.DisplayNames.
// Generating these means we never hand-maintain or translate proper language names, and
// a user scanning a picker always finds their language under the name they expect.
export function getLanguageLabel(code: LanguageCode): string {
  const name = new Intl.DisplayNames([code], { type: 'language' }).of(code) ?? code;
  // Capitalize the leading letter for consistent dropdown casing. Several locales
  // (es, fr, pt) lowercase language names by convention, but a leading capital reads
  // better in a list; this is a no-op for scripts without letter case (e.g. Japanese).
  return name.charAt(0).toLocaleUpperCase(code) + name.slice(1);
}

export interface LanguageOption {
  value: LanguageCode;
  label: string;
}

// A language's name in English (e.g. 'Arabic', 'German', 'Brazilian Portuguese'). Used only
// to ORDER the picker: sorting by a single fixed reference language gives a stable, locale-
// independent A–Z order, so the list never reshuffles as the UI language changes. The labels
// users actually see stay endonyms (see getLanguageLabel).
function getLanguageEnglishName(code: LanguageCode): string {
  return new Intl.DisplayNames(['en'], { type: 'language' }).of(code) ?? code;
}

// The base (language-only) subtag of a code, e.g. 'pt' for both 'pt' and 'pt-BR'. Used to
// GROUP regional variants together in the picker: ordering by the base language's English
// name keeps 'pt' and 'pt-BR' adjacent under "Portuguese" instead of scattering 'pt-BR'
// under its own English name ("Brazilian Portuguese").
function getBaseCode(code: LanguageCode): string {
  return code.split('-')[0];
}

// Language dropdown options (code + endonym label) for a given set of codes. The default
// language (English) is pinned first; the rest follow A–Z by their base language's English
// name, which keeps regional variants grouped (e.g. 'pt' then 'pt-BR' together under
// "Portuguese") with the generic variant ahead of its regional ones. Callers pass their
// app's availableLanguages so the picker only offers languages the app actually ships; it
// defaults to the full global registry for any standalone use. Shared by the LanguageSwitcher
// and the account profile dropdown so the two never diverge.
export function getLanguageOptions(codes: readonly LanguageCode[] = SUPPORTED_LANGUAGE_CODES): LanguageOption[] {
  return codes
    .map((code) => ({ value: code, label: getLanguageLabel(code) }))
    .sort((a, b) => {
      // Pin the default language (English) to the top regardless of alphabetical order.
      if (a.value === DEFAULT_LANGUAGE) return -1;
      if (b.value === DEFAULT_LANGUAGE) return 1;
      // Primary order: the base language's English name, so variants of one language cluster.
      const byBase = getLanguageEnglishName(getBaseCode(a.value) as LanguageCode).localeCompare(
        getLanguageEnglishName(getBaseCode(b.value) as LanguageCode),
        'en',
      );
      if (byBase !== 0) return byBase;
      // Same base language: the generic variant (code === base) comes before regional ones,
      // then remaining ties by the raw BCP 47 code rather than the English display name.
      // Intl's display-name format is inconsistent across variants ("Simplified Chinese" vs
      // "Chinese (Traditional, Taiwan)"), so sorting by code keeps within-group order
      // deterministic and predictable (e.g. zh-Hans before zh-Hant before zh-Hant-TW).
      const aGeneric = a.value === getBaseCode(a.value);
      const bGeneric = b.value === getBaseCode(b.value);
      if (aGeneric !== bGeneric) return aGeneric ? -1 : 1;
      return a.value.localeCompare(b.value, 'en');
    });
}
